<template>
  <CollapsibleRoot
    v-model:open="open"
    :data-collapsed="!open"
    class="group/tabs flex flex-col items-center justify-right bg-primary transition-all duration-200"
    :class="open ? 'w-[400px]' : 'w-12'"
  >
    <Component
      :is="open ? 'div' : CollapsibleTrigger"
      class="w-full flex items-start justify-center"
      :class="{ 'border-b border-secondary': open }"
    >
      <Tabs
        v-model="activeTabIndex"
        :orientation="open ? 'horizontal' : 'vertical'"
        variant="taskDetails"
        tabVariant="underline"
        :hideContent="true"
        class="w-full"
      >
        <template #leading>
          <TaskTabsToggle v-show="!open" :active="true" />
        </template>
        <Tab v-for="tab in tabs" :key="tab.id" v-bind="tab" />
        <template #end>
          <CollapsibleTrigger
            v-show="open"
            :active="false"
            :as="TaskTabsToggle"
          />
        </template>
      </Tabs>
    </Component>
    <CollapsibleContent
      class="w-full flex flex-col items-center grow overflow-hidden"
    >
      <Tabs
        v-model="activeTabIndex"
        variant="taskDetails"
        :hideNavigation="true"
        class="w-full h-full"
      >
        <Tab :label="$t('components.task_details.tab.activity_log')">
          <Activity :task="task" :feed="feed" />
        </Tab>
        <Tab
          :label="$t('components.task_details.tab.comments')"
          class="flex flex-col justify-end items-end"
        >
          <Comments
            :task="task"
            :feed="feed"
            :users="users"
            :media="commentsMedia"
            :readonly="isArchived"
          />
        </Tab>
        <Tab :label="$t('components.task_details.tab.attachments')">
          <Attachments :attachments="props.attachments" />
        </Tab>
      </Tabs>
    </CollapsibleContent>
  </CollapsibleRoot>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
} from "radix-vue";
import { Tab, Tabs } from "@/SharedComponents/Tabs";
import { Activity, Attachments, Comments } from "@/SharedComponents/Tasks/Tabs";
import { TaskTabsToggle } from "@/SharedComponents/Tasks/TaskDetailsParts";
import { useI18n } from "vue-i18n";
import { isNull } from "lodash";

const { t } = useI18n();

const emit = defineEmits(["update:state"]);

const props = defineProps({
  open: { type: Boolean, required: false },
  tabIndex: { type: [String, Number], required: false },
  task: { type: Object, required: true },
  feed: { type: Array, required: true },
  attachments: { type: Array, required: true },
  users: { type: Array, required: true },
  commentsMedia: { type: Array, default: () => [] },
  isArchived: { type: Boolean, default: false },
});

const open = ref(props.open);
const activeTabIndex = ref(props.tabIndex || 0);

const tabs = computed(() => [
  activity.value,
  comments.value,
  attachments.value,
]);

const activeTab = computed(() => tabs.value[activeTabIndex.value]);

const comments = computed(() => {
  let comments = { id: "comments" };
  if (open.value) {
    comments.badge = commentsCount.value || "0";
    comments.label = t("components.task_details.tab.comments");
  } else {
    comments.icon = "chat";
  }
  return comments;
});

const activity = computed(() => {
  let activity = { id: "activity" };
  if (open.value) {
    activity.label = t("components.task_details.tab.activity_log");
  } else {
    activity.icon = "clock-rewind";
  }
  return activity;
});

const attachments = computed(() => {
  let attachments = { id: "attachments" };
  if (open.value) {
    attachments.badge = attachmentsCount.value || "0";
    attachments.label = t("components.task_details.tab.attachments");
  } else {
    attachments.icon = "attachment";
  }
  return attachments;
});

const attachmentsCount = computed(() =>
  props.attachments.reduce((acc, attachment) => {
    return acc + attachment.items?.length;
  }, 0)
);

const commentsCount = computed(
  () => props.feed.filter((item) => !isNull(item.comment))?.length
);

watch(
  () => [open.value, activeTab.value],
  ([open, tab]) => {
    emit("update:state", {
      collapsed: !open,
      selected: tab.id,
    });
  }
);

defineExpose({
  open: computed(() => open.value),
});
</script>
